import * as Sentry from '@sentry/vue';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { attachNormalized, normalizeError } from './normalizeError';
import { redactUrl } from './redact';
import { ErrorKind, ErrorSource, type NormalizedError } from './errorTypes';
import { pushTrail } from './trail';

/**
 * The HTTP seam.
 *
 * `normalizeError` is the universal seam — it sees every error source. The interceptor owns only what
 * must happen once per response and must happen *even when nobody catches*:
 *
 *  1. 401 session-expiry -> logout. This used to live inside `LoadingStateModel.setError`, so the
 *     store calls without a try/catch never triggered it: an expired session on app load produced an
 *     unhandled rejection and a spinner that never stopped. Here it covers all 24 services at once.
 *  2. Trace correlation, captured at throw time while the context still exists.
 *  3. The trail entry, so a report can show what led up to the failure.
 *
 * It **decorates, never replaces**: the original `AxiosError` is re-rejected, because ~20 call sites
 * do `error instanceof AxiosError`. `normalizeError` reads the attached classification back, so the
 * work happens once even though it is called twice.
 */

export interface HttpErrorHooks {
  onSessionExpired: () => void;
}

/**
 * The hooks the installed interceptor runs with, kept so a request that deliberately bypasses
 * `apiClient` can reach them through `reportBypassedHttpError`.
 *
 * Module-level rather than passed around, because the bypassing call sites are services that have
 * no way to reach `main.ts`'s hooks and no business knowing what they are. `undefined` until
 * `installHttpErrorInterceptor` runs, which is before any service can fire a request.
 */
let installedHooks: HttpErrorHooks | undefined;

const START_KEY = '__appStartedAt';

type TimedConfig = InternalAxiosRequestConfig & { [START_KEY]?: number };

const durationOf = (config?: TimedConfig): number | undefined => {
  const startedAt = config?.[START_KEY];
  return typeof startedAt === 'number' ? Date.now() - startedAt : undefined;
};

const labelFor = (normalized: NormalizedError): string =>
  `${normalized.request?.method ?? 'GET'} ${normalized.request?.path ?? '?'}`;

/**
 * The three jobs the interceptor owns, for one failed response.
 *
 * Extracted so `reportBypassedHttpError` runs exactly this and cannot drift from it. It never
 * throws: bookkeeping must not turn one failure into two, and the caller still rejects or throws
 * the original error afterwards.
 */
const decorateHttpError = (error: unknown, hooks: HttpErrorHooks): void => {
  try {
    const normalized = normalizeError(error, { source: ErrorSource.HTTP });

    if (normalized.request) {
      normalized.request.durationMs = durationOf(
        (error as { config?: TimedConfig }).config
      );
      normalized.request.sentryTrace = Sentry.getTraceData()['sentry-trace'];
      // NOTE: only present if the backend sets it. Confirm with ts-api before relying on it.
      const headers = (error as { response?: { headers?: Record<string, string> } }).response?.headers;
      normalized.request.requestId = headers?.['x-request-id'];
    }

    pushTrail({
      type: 'request',
      label: labelFor(normalized),
      status: normalized.httpStatus,
      ms: normalized.request?.durationMs
    });

    if (error != null && typeof error === 'object') {
      attachNormalized(error, normalized);
    }

    if (normalized.kind === ErrorKind.AUTH_EXPIRED) {
      hooks.onSessionExpired();
    }
  } catch {
    /* eslint-disable-next-line no-console */
    console.error('[errors] response interceptor failed');
  }
};

/**
 * Run the interceptor's handling for a failure that never went through `apiClient`.
 *
 * For the handful of requests that bypass it on purpose — today `streamPerfusionDashboard`, which
 * needs `fetch` because axios buffers the body it reads as a stream. Without this, an expired
 * session on such a request reached nobody: no logout, no trail entry, and a normalization the
 * error store would redo from scratch.
 *
 * Pass an `AxiosError`. `normalizeError` reads status, path and body off that shape, so anything
 * else classifies as `UNKNOWN` and a 401 goes unrecognised — which is the bug this exists to fix.
 *
 * A no-op before the interceptor is installed, which is the same window in which nothing can have
 * fired a request yet.
 */
export const reportBypassedHttpError = (error: unknown): void => {
  if (!installedHooks) {
    return;
  }
  decorateHttpError(error, installedHooks);
};

export const installHttpErrorInterceptor = (client: AxiosInstance, hooks: HttpErrorHooks): void => {
  installedHooks = hooks;

  client.interceptors.request.use(config => {
    (config as TimedConfig)[START_KEY] = Date.now();
    return config;
  });

  client.interceptors.response.use(
    response => {
      try {
        pushTrail({
          type: 'request',
          label: `${response.config?.method?.toUpperCase() ?? 'GET'} ${redactUrl(response.config?.url)?.path ?? '?'}`,
          status: response.status,
          ms: durationOf(response.config as TimedConfig)
        });
      } catch {
        // Never let bookkeeping turn a successful response into a failure.
      }
      return response;
    },
    (error: unknown) => {
      decorateHttpError(error, hooks);

      // Unchanged, so `instanceof AxiosError` keeps working at every existing call site.
      return Promise.reject(error);
    }
  );
};
